import type { ClientGitStatus } from "@app/commands/versionControl/statuses";
import ArticleUpdaterService from "@components/Article/ArticleUpdater/ArticleUpdaterService";
import ApiUrlCreatorService from "@core-ui/ContextServices/ApiUrlCreator";
import ModalToOpenService from "@core-ui/ContextServices/ModalToOpenService/ModalToOpenService";
import { useApi } from "@core-ui/hooks/useApi";
import { useBranchCheckoutSettingsStore } from "@ext/git/actions/Branch/BranchPanel/logic/BranchCheckoutSettingsStore";
import { useBranchCheckout } from "@ext/git/actions/Branch/BranchPanel/logic/useBranchCheckout";
import BranchUpdaterService from "@ext/git/actions/Branch/BranchUpdaterService/logic/BranchUpdaterService";
import { useState } from "react";

/** Checkout of the branch a link points to: the confirmation modal state and the checkout itself. */
export const useLinkCheckout = (branchToCheckout: string) => {
	const apiUrlCreator = ApiUrlCreatorService.value;
	const [isOpen, setIsOpen] = useState(true);
	const [checkoutProcess, setCheckoutProcess] = useState(false);
	const [hideModeChoice, setHideModeChoice] = useState(false);
	const { checkoutBranch, checkoutDialog } = useBranchCheckout();
	const mode = useBranchCheckoutSettingsStore((state) => state.mode);
	const hideConfirmation = useBranchCheckoutSettingsStore((state) => state.hideConfirmation);
	const { data: localChanges } = useApi<ClientGitStatus[]>({
		url: (api) => api.getVersionControlStatuses(false),
		opts: { consumeError: true },
		auto: true,
	});
	const hasLocalChanges = !!localChanges?.length;
	const transferChanges = mode === "transfer";
	const checkoutBlocked = hasLocalChanges && !transferChanges;

	const cancel = () => {
		ModalToOpenService.resetValue();
		setIsOpen(false);
	};

	const onOpenChange = (open: boolean) => {
		if (checkoutProcess) return;
		setIsOpen(open);
		if (!open) ModalToOpenService.resetValue();
	};

	const checkout = async () => {
		setCheckoutProcess(true);
		try {
			const res = await checkoutBranch(
				branchToCheckout,
				hideConfirmation ? undefined : { mode, hideConfirmation: hideModeChoice },
			);
			if (!res) return;
			if (!res.ok) {
				cancel();
				return;
			}
			setIsOpen(false);
			await BranchUpdaterService.updateBranch(apiUrlCreator);
			await ArticleUpdaterService.update(apiUrlCreator);
			ModalToOpenService.resetValue();
		} finally {
			setCheckoutProcess(false);
		}
	};

	return {
		isOpen: isOpen && !checkoutDialog,
		checkoutDialog,
		checkoutProcess,
		checkoutBlocked,
		transferChanges,
		hasLocalChanges,
		hideConfirmation,
		hideModeChoice,
		setHideModeChoice,
		cancel,
		onOpenChange,
		checkout,
	};
};
